import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { sendClientOnboardingEmail } from '../utils/emailJsPortal';
import Layout from '../components/Layout';
import './ClientsList.css';

const INVITE_EXPIRY_DAYS = 30;

const ClientsList = () => {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const isAdmin = role === 'admin';
  const [profiles, setProfiles] = useState([]);
  const [productCounts, setProductCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [showStakeholderForm, setShowStakeholderForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [stakeholderEmail, setStakeholderEmail] = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteType, setInviteType] = useState(null); // 'client' | 'stakeholder'
  const [onboardingEmailSent, setOnboardingEmailSent] = useState(false);
  const [onboardingEmailError, setOnboardingEmailError] = useState(null);
  const [onboardingEmailSkipped, setOnboardingEmailSkipped] = useState(false);
  const [onboardingEmailMissing, setOnboardingEmailMissing] = useState([]);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: profilesData, error: profilesError } = await supabase
        .from('client_profiles')
        .select('id, company_name, brand_supplier_name, email, first_name, last_name, setup_completed_at, created_at')
        .order('created_at', { ascending: false });

      if (profilesError) {
        setError(profilesError.message);
        setProfiles([]);
        setLoading(false);
        return;
      }

      setProfiles(profilesData || []);

      if ((profilesData || []).length > 0) {
        const { data: countsData } = await supabase
          .from('client_products')
          .select('client_profile_id');
        const counts = {};
        (countsData || []).forEach((row) => {
          counts[row.client_profile_id] = (counts[row.client_profile_id] || 0) + 1;
        });
        setProductCounts(counts);
      }
    } catch (err) {
      setError(err.message || 'Failed to load clients');
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInviteLink = async (e, type) => {
    e.preventDefault();
    setAddError('');
    if (!user?.id) {
      setAddError('Not logged in.');
      return;
    }
    const emailVal = type === 'stakeholder' ? stakeholderEmail.trim() : inviteEmail.trim();
    setAdding(true);
    try {
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + INVITE_EXPIRY_DAYS);
      const { data, error: insertErr } = await supabase
        .from('client_registration_links')
        .insert({
          email: emailVal || null,
          expires_at: expiresAt.toISOString(),
          created_by: user.id,
          link_type: type === 'stakeholder' ? 'stakeholder' : 'client',
        })
        .select('id')
        .single();
      if (insertErr) {
        setAddError(insertErr.message || 'Failed to create invite link');
        setAdding(false);
        return;
      }
      const url = type === 'stakeholder'
        ? `${window.location.origin}/register/${data.id}?type=stakeholder`
        : `${window.location.origin}/register/${data.id}`;
      setInviteLink({ id: data.id, url, email: type === 'client' ? emailVal || null : null });
      setInviteType(type);
      setInviteEmail('');
      setStakeholderEmail('');
      setOnboardingEmailSent(false);
      setOnboardingEmailError(null);
      setOnboardingEmailSkipped(false);
      setOnboardingEmailMissing([]);
      if (type === 'client' && emailVal) {
        const result = await sendClientOnboardingEmail({ to_email: emailVal, signup_url: url });
        if (result.ok) {
          setOnboardingEmailSent(true);
        } else if (result.skipped) {
          setOnboardingEmailSkipped(true);
          setOnboardingEmailMissing(result.missing);
        } else {
          setOnboardingEmailError(result.error || 'Failed to send email');
        }
      }
    } catch (err) {
      setAddError(err?.message || 'Failed to create invite link');
    }
    setAdding(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="clients-list">
          <h1>Clients</h1>
          <p className="clients-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="clients-list">
        <div className="clients-header">
          <h1>Clients</h1>
          {isAdmin && (
            <div className="clients-header-actions">
              <Link to="/clients/invites" className="clients-invite-history-link">Invite history</Link>
              <button
                type="button"
                className="clients-add-btn"
                onClick={() => {
                  setShowStakeholderForm(false);
                  setShowAddForm(!showAddForm);
                  setAddError('');
                  setInviteLink(null);
                  setOnboardingEmailSent(false);
                  setOnboardingEmailError(null);
                  setOnboardingEmailSkipped(false);
                  setOnboardingEmailMissing([]);
                }}
              >
                {showAddForm ? 'Cancel' : '+ Add client'}
              </button>
              <button
                type="button"
                className="clients-add-btn"
                onClick={() => {
                  setShowAddForm(false);
                  setShowStakeholderForm(!showStakeholderForm);
                  setAddError('');
                  setInviteLink(null);
                  setOnboardingEmailSent(false);
                  setOnboardingEmailError(null);
                  setOnboardingEmailSkipped(false);
                  setOnboardingEmailMissing([]);
                }}
              >
                {showStakeholderForm ? 'Cancel' : '+ Invite stakeholder'}
              </button>
            </div>
          )}
        </div>

        {inviteLink && (
          <div className="clients-credentials-box">
            <p className="clients-credentials-warning">
              Send this one-time link to the {inviteType === 'stakeholder' ? 'stakeholder' : 'client'}. It expires in {INVITE_EXPIRY_DAYS} days and can only be used once.
            </p>
            {inviteType === 'client' && inviteLink.email && (
              <>
                {onboardingEmailSent && (
                  <p className="clients-onboarding-email clients-onboarding-email-sent">Onboarding email sent to {inviteLink.email}.</p>
                )}
                {onboardingEmailSkipped && (
                  <p className="clients-onboarding-email clients-onboarding-email-error">
                    Onboarding email was not sent (not configured). The app reports these env vars as missing or empty: <strong>{Array.isArray(onboardingEmailMissing) && onboardingEmailMissing.length ? onboardingEmailMissing.join(', ') : 'REACT_APP_EMAILJS_ONBOARDING_TEMPLATE_ID, REACT_APP_EMAILJS_SERVICE_ID, REACT_APP_EMAILJS_PUBLIC_KEY'}</strong>. Check that <code>.env</code> lives in the <code>marketvendr-dashboard</code> folder, variable names are exact (no typos), there are no quotes around the value, and restart the dev server (<code>npm start</code>). Copy the link below to send manually.
                  </p>
                )}
                {onboardingEmailError && (
                  <p className="clients-onboarding-email clients-onboarding-email-error">Email could not be sent: {onboardingEmailError}. You can still copy the link below and send it manually.</p>
                )}
              </>
            )}
            <div className="clients-credentials-fields">
              <div className="clients-credentials-row">
                <span className="clients-credentials-label">Registration link</span>
                <code className="clients-credentials-value" style={{ wordBreak: 'break-all' }}>{inviteLink.url}</code>
              </div>
            </div>
            <button
              type="button"
              className="clients-credentials-done"
              onClick={() => {
                navigator.clipboard?.writeText(inviteLink.url);
                setInviteLink(null);
                setInviteType(null);
                setOnboardingEmailSent(false);
                setOnboardingEmailError(null);
                setOnboardingEmailSkipped(false);
                setOnboardingEmailMissing([]);
                setShowAddForm(false);
                setShowStakeholderForm(false);
              }}
            >
              Copy and close
            </button>
          </div>
        )}
        {error && <div className="clients-error">{error}</div>}

        {showAddForm && !inviteLink && isAdmin && (
          <form onSubmit={(e) => handleGenerateInviteLink(e, 'client')} className="clients-add-form">
            <h2 className="clients-add-form-title">Invite client</h2>
            <p className="clients-add-form-intro">
              Enter the client’s email to automatically send them an email containing the signup URL and instructions. They set their own password and complete company setup after first login. If you leave email blank, you can copy the link and send it to them yourself. The link expires in {INVITE_EXPIRY_DAYS} days and can only be used once.
            </p>
            {addError && <div className="clients-error">{addError}</div>}
            <div className="clients-add-form-grid">
              <div className="clients-add-field">
                <label htmlFor="invite-email">Client email (optional)</label>
                <input
                  id="invite-email"
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="client@example.com"
                />
                <span className="clients-add-field-hint">If provided, we’ll automatically send them an email with the signup link and instructions.</span>
              </div>
            </div>
            <div className="clients-add-form-actions">
              <button type="submit" className="clients-add-submit" disabled={adding}>
                {adding ? 'Generating…' : 'Generate invite link'}
              </button>
            </div>
          </form>
        )}

        {showStakeholderForm && !inviteLink && isAdmin && (
          <form onSubmit={(e) => handleGenerateInviteLink(e, 'stakeholder')} className="clients-add-form">
            <h2 className="clients-add-form-title">Invite stakeholder</h2>
            <p className="clients-add-form-intro">
              Generate a one-time registration link for a stakeholder. They will log in to the dashboard (Admin login) and only see clients you assign to them in each client’s Visibility section. Optionally enter their email to pre-fill the registration form.
            </p>
            {addError && <div className="clients-error">{addError}</div>}
            <div className="clients-add-form-grid">
              <div className="clients-add-field">
                <label htmlFor="stakeholder-email">Stakeholder email (optional)</label>
                <input
                  id="stakeholder-email"
                  type="email"
                  value={stakeholderEmail}
                  onChange={(e) => setStakeholderEmail(e.target.value)}
                  placeholder="stakeholder@example.com"
                />
                <span className="clients-add-field-hint">If provided, the registration form will show this email pre-filled.</span>
              </div>
            </div>
            <div className="clients-add-form-actions">
              <button type="submit" className="clients-add-submit" disabled={adding}>
                {adding ? 'Generating…' : 'Generate invite link'}
              </button>
            </div>
          </form>
        )}

        {profiles.length === 0 ? (
          <div className="clients-empty">
            <p>
              {isAdmin
                ? 'No client profiles yet. Clients appear here after they complete company setup in the Vendr portal.'
                : "You don't have access to any clients yet. An admin can add you to a client's Visibility section."}
            </p>
          </div>
        ) : (
          <div className="clients-table-container">
            <table className="clients-table">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Contact</th>
                  <th>Setup completed</th>
                  <th>Products</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr
                    key={profile.id}
                    className="clients-table-row"
                    onClick={() => navigate(`/clients/${profile.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/clients/${profile.id}`);
                      }
                    }}
                  >
                    <td>
                      <span className="clients-company-link">{profile.company_name || '—'}</span>
                    </td>
                    <td>
                      {profile.email || '—'}
                      {(profile.first_name || profile.last_name) && (
                        <span className="clients-contact-name">
                          {' '}({[profile.first_name, profile.last_name].filter(Boolean).join(' ')})
                        </span>
                      )}
                    </td>
                    <td>
                      {profile.setup_completed_at
                        ? new Date(profile.setup_completed_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td>{productCounts[profile.id] ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientsList;
